import React, { useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, ArrowUpRight, Settings2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { EditableText, EditableImage, HrefPill, useEditMode } from '../../components';

/**
 * navbar — pixel-identical reproduction of src/components/Navbar.js
 * but data-driven from CMS globals.
 */
function NavbarBlock({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const updateStrip = (patch) => update({ top_strip: { ...(data?.top_strip || {}), ...patch } });
  const updateLogo = (patch) => update({ logo: { ...(data?.logo || {}), ...patch } });
  const updateCtaField = (patch) => update({ cta: { ...(data?.cta || {}), ...patch } });
  const updateItem = (i, patch) => {
    const list = (data?.items || []).map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: list });
  };
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const { editMode } = useEditMode();
  const lightMode = false;
  const items = data?.items || [];
  const logo = data?.logo || {};
  const strip = data?.top_strip || {};
  const cta = data?.cta || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 pointer-events-none pt-12 sm:pt-0">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: scrolled ? 0 : 1, y: scrolled ? -10 : 0, height: scrolled ? 0 : 'auto' }}
        transition={{ duration: 0.4 }}
        className="pointer-events-auto overflow-hidden bg-olive-footer text-white/85 hidden sm:block"
      >
        <div className="container-page flex items-center justify-between py-2 text-[10px] font-mono tracking-[0.28em] uppercase">
          <div
            className="flex items-center gap-3"
            style={{ paddingLeft: `calc(${(logo.size || 120) + 16}px)` }}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-80" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-accent" />
            </span>
            <EditableText value={strip.brand_long} onChange={(t) => updateStrip({ brand_long: t })} as="span" className="hidden sm:inline" />
            <EditableText value={strip.brand_short} onChange={(t) => updateStrip({ brand_short: t })} as="span" className="sm:hidden font-bold text-accent" />
            <span className="text-accent/70">·</span>
            <EditableText value={strip.tagline} onChange={(t) => updateStrip({ tagline: t })} as="span" className="hidden md:inline text-white/55" />
          </div>
          <div className="hidden lg:flex items-center gap-5 text-white/70">
            <div className="flex flex-col">
              <a href={strip.email_href} className="hover:text-accent transition"><EditableText value={strip.email} onChange={(t) => updateStrip({ email: t })} as="span" /></a>
              {editMode && editorOpen && <HrefPill value={strip.email_href} onChange={(h) => updateStrip({ email_href: h })} placeholder="mailto: link" />}
            </div>
            <span className="text-white/20">/</span>
            <div className="flex flex-col">
              <a href={strip.phone_href} className="hover:text-accent transition"><EditableText value={strip.phone} onChange={(t) => updateStrip({ phone: t })} as="span" /></a>
              {editMode && editorOpen && <HrefPill value={strip.phone_href} onChange={(h) => updateStrip({ phone_href: h })} placeholder="tel: link" />}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="pointer-events-auto">
        <div className="container-page">
          <motion.div
            layout
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`relative rounded-[2rem] bg-white border border-olive/15 transition-all duration-500 ${
              scrolled
                ? 'shadow-[0_20px_60px_-20px_rgba(46,58,24,0.25)] px-5 py-0'
                : 'shadow-[0_20px_60px_-20px_rgba(46,58,24,0.18)] px-6 py-0'
            }`}
          >
            <span className="absolute -top-px left-12 right-12 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />

            {/* Line 2 — Complete logo on the left */}
            <div className="relative flex items-center justify-between gap-4">
              <Link to="/" onClick={close} className="flex items-center group shrink-0 self-center">
                <div
                  className={`relative flex items-center justify-center rounded-full bg-white shadow-[0_10px_30px_-10px_rgba(46,58,24,0.35)] ring-1 ring-olive/10 transition-all duration-500 ${
                    scrolled ? 'my-1 p-1.5' : '-my-8 sm:-my-10 p-2.5'
                  }`}
                  style={{
                    width: scrolled ? (logo.size_scrolled || 56) : (logo.size || 120),
                    height: scrolled ? (logo.size_scrolled || 56) : (logo.size || 120),
                  }}
                >
                  <div className="absolute inset-0 rounded-full bg-accent/40 blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                  <EditableImage
                    src={logo.src}
                    alt={logo.alt || 'Logo'}
                    onChange={(src, alt) => updateLogo({ src, alt })}
                    className="relative max-h-full max-w-full w-auto h-auto object-contain drop-shadow-xl"
                  />
                </div>
                <EditableText
                  value={strip.brand_short && strip.brand_short.length > 6 ? strip.brand_short : 'TRIVANDRUM SCOTTISH SCHOOL'}
                  onChange={(t) => updateStrip({ brand_short: t })}
                  as="span"
                  className="ml-3 sm:hidden font-display font-bold text-olive-deep text-[13px] leading-tight tracking-tight"
                />
              </Link>

              {/* Menu items inline with logo + login */}
              <nav className="hidden lg:flex flex-nowrap items-center justify-center gap-x-1 flex-1 min-w-0">
                {items.map((item, idx) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.href === '/'}
                    className={({ isActive }) =>
                      `relative whitespace-nowrap px-2.5 py-2 text-[12.5px] font-medium tracking-tight transition-colors rounded-full ${
                        isActive
                          ? (lightMode ? 'text-accent' : 'text-olive-deep')
                          : (lightMode ? 'text-white/80 hover:text-accent' : 'text-ink/70 hover:text-olive-deep')
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative z-10"><EditableText value={item.label} onChange={(t) => updateItem(idx, { label: t })} as="span" /></span>
                        {isActive && (
                          <motion.span
                            layoutId="nav-underline"
                            className="absolute inset-x-2 -bottom-[3px] h-[3px] rounded-full bg-gradient-to-r from-olive via-accent to-olive shadow-[0_0_14px_rgba(197,120,99,0.85)]"
                          />
                        )}
                        {isActive && <motion.span layoutId="nav-glow" className="absolute inset-0 rounded-full bg-accent/0" />}
                      </>
                    )}
                  </NavLink>
                ))}
              </nav>

              <div className="flex items-center gap-2 shrink-0">
                {cta?.label && (
                  <div className="hidden md:inline-flex flex-col">
                    <Link
                      to={cta.href || '#'}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em] border transition-all duration-300 ${
                        lightMode
                          ? 'border-white/30 text-white hover:bg-white/10 hover:border-accent/60 hover:text-accent'
                          : 'border-olive/30 text-olive-deep hover:bg-olive-light hover:border-olive/60'
                      }`}
                    >
                      <EditableText value={cta.label} onChange={(t) => updateCtaField({ label: t })} as="span" />
                      <ArrowUpRight size={12} className="transition-transform group-hover:rotate-45" />
                    </Link>
                    {editMode && editorOpen && <HrefPill value={cta.href} onChange={(h) => updateCtaField({ href: h })} />}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  className={`lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full transition ${
                    lightMode
                      ? 'bg-white/10 text-white border border-white/20 hover:bg-accent hover:text-ink hover:border-accent'
                      : 'bg-olive-light text-olive-deep hover:bg-olive hover:text-white'
                  }`}
                  aria-label="Toggle navigation"
                >
                  {open ? <X size={22} /> : <Menu size={22} />}
                </button>
              </div>
            </div>

          </motion.div>
          {editMode && editorOpen && (
            <div className="mt-2 p-3 rounded-2xl bg-yellow-50 border border-yellow-200 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <label className="flex flex-col text-[10px] font-mono text-ink/70">
                  <span>Logo size (default)</span>
                  <input
                    type="number"
                    min="40"
                    max="200"
                    value={logo.size || 120}
                    onChange={(e) => updateLogo({ size: Number(e.target.value) || 120 })}
                    className="mt-0.5 bg-white border border-yellow-300 rounded px-2 py-0.5"
                  />
                </label>
                <label className="flex flex-col text-[10px] font-mono text-ink/70">
                  <span>Logo size (scrolled)</span>
                  <input
                    type="number"
                    min="32"
                    max="120"
                    value={logo.size_scrolled || 56}
                    onChange={(e) => updateLogo({ size_scrolled: Number(e.target.value) || 56 })}
                    className="mt-0.5 bg-white border border-yellow-300 rounded px-2 py-0.5"
                  />
                </label>
                <label className="flex flex-col text-[10px] font-mono text-ink/70">
                  <span>Logo alt text</span>
                  <input
                    type="text"
                    value={logo.alt || ''}
                    onChange={(e) => updateLogo({ alt: e.target.value })}
                    className="mt-0.5 bg-white border border-yellow-300 rounded px-2 py-0.5"
                  />
                </label>
              </div>
              {items.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                  {items.map((item, i) => (
                    <label key={i} className="flex flex-col text-[10px] font-mono text-ink/70">
                      <span className="truncate">{item.label || `Item ${i + 1}`}</span>
                      <input
                        type="text"
                        value={item.href || ''}
                        onChange={(e) => updateItem(i, { href: e.target.value })}
                        className="mt-0.5 bg-white border border-yellow-300 rounded px-2 py-0.5"
                        placeholder="href"
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pointer-events-auto lg:hidden fixed inset-0 top-[110px] bg-olive-footer/70 backdrop-blur-sm"
            onClick={close}
          >
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
              className="absolute right-3 top-0 bottom-3 w-[88%] max-w-sm rounded-3xl bg-white shadow-2xl border border-olive/10 overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-olive mb-5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-80" />
                    <span className="relative rounded-full h-1.5 w-1.5 bg-accent" />
                  </span>
                  Navigate
                </div>
                <nav className="flex flex-col gap-1">
                  {items.map((item, i) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      end={item.href === '/'}
                      onClick={close}
                      className={({ isActive }) =>
                        `flex items-center justify-between px-4 py-3 rounded-2xl text-base font-medium transition group ${
                          isActive ? 'bg-olive-light text-olive-deep' : 'text-ink hover:bg-olive-light/60'
                        }`
                      }
                      style={{ animation: `fadeUp .35s ${i * 0.04}s both` }}
                    >
                      {({ isActive }) => (
                        <>
                          <EditableText value={item.label} onChange={(t) => updateItem(i, { label: t })} as="span" />
                          <ArrowUpRight size={16} className={`transition-all ${isActive ? 'text-accent' : 'text-ink/40 group-hover:text-olive opacity-0 group-hover:opacity-100'}`} />
                        </>
                      )}
                    </NavLink>
                  ))}
                </nav>
                {cta?.label && (
                  <div className="border-t border-olive/10 mt-5 pt-5 flex flex-col gap-3">
                    <Link to={cta.href || '#'} onClick={close} className="btn-ghost w-full"><EditableText value={cta.label} onChange={(t) => updateCtaField({ label: t })} as="span" /></Link>
                  </div>
                )}
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {editMode && (
        <button
          type="button"
          onClick={() => setEditorOpen((v) => !v)}
          className="pointer-events-auto fixed bottom-5 right-5 z-[60] inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-indigo-700 text-white text-xs font-semibold shadow-lg hover:bg-indigo-800 transition"
          title={editorOpen ? 'Hide nav editor' : 'Edit nav links & contact'}
        >
          <Settings2 size={14} />
          {editorOpen ? 'Hide nav editor' : 'Edit nav links'}
        </button>
      )}
    </header>
  );
}

export default NavbarBlock;
